import { API_BASE_URL, AUTH_MODE, ApiError, cookieRequest } from '@/services/api';

export interface FamilyMember {
  id: string;
  name: string;
  role: string;
  birthDate: string;
  avatar: string;
  sortOrder: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface ProfileStats {
  albumCount: number;
  photoCount: number;
  recordDays: number;
}

interface InviteCodeResponse {
  inviteCode: string;
}

export interface FamilyMemberPayload {
  name: string;
  role: string;
  birthDate: string;
  sortOrder?: number;
}

const MOCK_MEMBERS_KEY = 'bongnas_family_members';

const DEFAULT_MEMBERS: FamilyMember[] = [
  { id: '1', name: '김민준', role: '아빠', birthDate: '1987.05.20', avatar: '', sortOrder: 1 },
  { id: '2', name: '박지은', role: '엄마', birthDate: '1989.09.12', avatar: '', sortOrder: 2 },
];

function readMockMembers(): FamilyMember[] {
  try {
    const raw = localStorage.getItem(MOCK_MEMBERS_KEY);
    return raw ? JSON.parse(raw) as FamilyMember[] : DEFAULT_MEMBERS;
  } catch {
    return DEFAULT_MEMBERS;
  }
}

function writeMockMembers(members: FamilyMember[]): void {
  localStorage.setItem(MOCK_MEMBERS_KEY, JSON.stringify(members));
}

export async function getFamilyMembers(): Promise<FamilyMember[]> {
  if (AUTH_MODE === 'mock') return readMockMembers();
  const response = await cookieRequest<{ members: FamilyMember[] }>('/api/family-members');
  return response.members;
}

export async function getProfileStats(): Promise<ProfileStats> {
  if (AUTH_MODE === 'mock') {
    return { albumCount: 0, photoCount: 0, recordDays: 0 };
  }
  const response = await cookieRequest<{ stats: ProfileStats }>('/api/profile-stats');
  return response.stats;
}

export async function getInviteCode(): Promise<string> {
  if (AUTH_MODE === 'mock') return '123456';
  const response = await cookieRequest<InviteCodeResponse>('/api/invite-code');
  return response.inviteCode;
}

export async function createFamilyMember(payload: FamilyMemberPayload): Promise<FamilyMember> {
  if (AUTH_MODE === 'mock') {
    const members = readMockMembers();
    const nextId = String(Math.max(0, ...members.map((member) => Number(member.id) || 0)) + 1);
    const member: FamilyMember = {
      ...payload,
      id: nextId,
      avatar: '',
      sortOrder: payload.sortOrder ?? members.length + 1,
    };
    writeMockMembers([...members, member]);
    return member;
  }
  const response = await cookieRequest<{ member: FamilyMember }>('/api/admin/family-members', {
    method: 'POST',
    body: JSON.stringify(payload),
  });
  return response.member;
}

export async function updateFamilyMember(
  memberId: string,
  payload: Partial<FamilyMemberPayload>,
): Promise<FamilyMember> {
  if (AUTH_MODE === 'mock') {
    const members = readMockMembers().map((member) => (
      member.id === memberId ? { ...member, ...payload } : member
    ));
    writeMockMembers(members);
    const updated = members.find((member) => member.id === memberId);
    if (!updated) throw new ApiError('가족 구성원을 찾을 수 없습니다.', 404);
    return updated;
  }
  const response = await cookieRequest<{ member: FamilyMember }>(
    `/api/admin/family-members/${memberId}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
  );
  return response.member;
}

export async function uploadFamilyMemberAvatar(memberId: string, file: File): Promise<FamilyMember> {
  if (AUTH_MODE === 'mock') {
    const avatar = URL.createObjectURL(file);
    const members = readMockMembers().map((member) => (
      member.id === memberId ? { ...member, avatar } : member
    ));
    writeMockMembers(members);
    const updated = members.find((member) => member.id === memberId);
    if (!updated) throw new ApiError('가족 구성원을 찾을 수 없습니다.', 404);
    return updated;
  }

  const formData = new FormData();
  formData.append('file', file);
  const response = await fetch(`${API_BASE_URL}/api/admin/family-members/${memberId}/avatar`, {
    method: 'POST',
    credentials: 'include',
    body: formData,
  });
  let body: unknown = null;
  try { body = await response.json(); } catch { /* 빈 응답 */ }
  if (!response.ok) {
    const record = body && typeof body === 'object' ? body as Record<string, unknown> : {};
    throw new ApiError(String(record.detail || record.message || '구성원 사진을 저장하지 못했습니다.'), response.status);
  }
  return (body as { member: FamilyMember }).member;
}

export async function deleteFamilyMember(memberId: string): Promise<void> {
  if (AUTH_MODE === 'mock') {
    writeMockMembers(readMockMembers().filter((member) => member.id !== memberId));
    return;
  }
  await cookieRequest(`/api/admin/family-members/${memberId}`, { method: 'DELETE' });
}
